import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { roleLabels } from "@/i18n/dictionaries";
import { roleHome } from "@/lib/roleHome";
import { JOIN_WORKER_PATH } from "@/lib/appLinks";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * The one way the web switches role (header menu and Account page share it — UAT W4/W5).
 *
 *  - W5: switching re-issues the session (`auth/role/switch`) and moves the person to another
 *    console, so it asks first instead of firing on the click.
 *  - W4: a Worker seat has no web destination (workers are app-only, D1). Choosing it never calls
 *    `auth/role/switch`; it opens the explainer that points at the Business app.
 *
 *  - R11 MULTICITY: the target may be a seat in another city (an entry of `auth.seats`, which
 *    carries `tenant_name`). The confirm names that city, and the switch lands on that city's
 *    console by a real navigation.
 *
 * Usage: `const { requestSwitch, dialog } = useRoleSwitchConfirm();` call `requestSwitch(roleProfile)`
 * and render `{dialog}` once.
 */
export function useRoleSwitchConfirm() {
  const { t, lang, switchRole, tenant } = useApp();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);

  const requestSwitch = (roleProfile) => {
    if (roleProfile.role_type === "worker") {
      navigate(JOIN_WORKER_PATH);
      return;
    }
    setPending(roleProfile);
  };

  const confirm = async () => {
    const target = pending;
    setPending(null);
    if (!target) return;
    const home = roleHome[target.role_type] || "/account";
    // A seat in ANOTHER city (R11 MULTICITY) is a real navigation to `/<city><home>` that
    // `switchRole` performs itself; only a same-city switch is a router `navigate()`.
    const data = await switchRole(target.id, { path: home });
    if (data?.navigated) return;
    navigate(home);
  };

  const roleName = pending ? roleLabels[lang]?.[pending.role_type] || pending.role_type : "";
  // Name the city when the target seat is not in the city on screen.
  const otherCity =
    pending && pending.tenant_id && tenant && pending.tenant_id !== tenant.id
      ? pending.tenant_name || ""
      : "";

  const dialog = (
    <ConfirmDialog
      open={Boolean(pending)}
      onOpenChange={(o) => !o && setPending(null)}
      title={t("account.switchRoleTitle")}
      body={t("account.switchRoleBody", {
        role: otherCity ? t("account.roleInCity", { role: roleName, city: otherCity }) : roleName,
      })}
      confirmLabel={t("account.switchRole")}
      onConfirm={confirm}
      testIds={{
        dialog: "role-switch-dialog",
        cancel: "role-switch-cancel-btn",
        ok: "role-switch-confirm-btn",
      }}
    />
  );

  return { requestSwitch, dialog };
}
